import {
  deleteRangeCC,
  deleteRangeSelection,
  getXmlByCCId,
  getXMLFromSelection,
  insertXmlIntoSelection,
  toggleDeleteAndEditCC,
} from "./WordXMLManager";
import {
  DocPropKeys,
  getDocumentPartMetadataFromCustomXml,
  retrieveDocumentProp,
} from "../utils/helpers/CustomXmlHelper";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../utils/constants/eventChannel";
import {
  applyComponentDataToLegacyComponentInstance,
  isLegacyComponentInstance,
} from "../utils/helpers/ComponentInstanceHelper";
import { insertElaboration, lockObject } from "../redux/feature/cpm/cpm-slice";
import { store } from "../redux/store/store";
import { selectCpmUsername } from "../redux/feature/settings/configurations-slice";
import { CoObjectItem } from "../components/interfaces/CoObject";
import { checkIfSelected, getParentContentControls } from "./ContentControl";
import { setPanelMessage } from "../redux/feature/panelMessage/panelMessage-slice";
import { invalidate } from "../redux/feature/validation/validation-slice";
import { CoObjectTypeEnum } from "../api/models";
import {
  getCoObjectFromTagOrCustomXmlByContentControlId,
  getDocumentCoObjects,
} from "../utils/helpers/ContentControlHelper";
import { fetchDocumentPartDetails } from "../api/document/DocumentsPart";
import { applyInheritedInfo } from "../utils/helpers/InheritanceHelper";
import { updateContentControlAliases } from "./xmlDecoder";

// Alias for Office.js type
import ContentControl = Word.ContentControl;

export interface DeletableAndEditableCC {
  id: Word.ContentControl["id"];
  cannotDelete: Word.ContentControl["cannotDelete"];
  cannotEdit: Word.ContentControl["cannotEdit"];
}

interface PastedCCResult {
  oldId?: Word.ContentControl["id"]; // optional: may not be available
  id?: Word.ContentControl["id"]; // optional: may not be available
  title: Word.ContentControl["title"];
}

export enum ActionType {
  CUT = "CUT",
  COPY = "COPY",
}

class CopyCutPaste {
  private static instance: CopyCutPaste | null = null;

  public static getInstance() {
    if (!this.instance) this.instance = new CopyCutPaste();
    return this.instance;
  }

  private action: ActionType | null = null;
  private CCsSelected: DeletableAndEditableCC[] = [];
  private ccsXmlString = "";
  private isPasteInProgress = false;
  private lastPasteCompletedAt = 0;
  private static readonly PASTE_COOLDOWN_MS = 500;

  async cut(): Promise<boolean> {
    const ccs = await this.getCCSelected();
    if (!ccs?.length) return false;

    // A new cut starts a fresh clipboard flow, so allow immediate paste.
    this.lastPasteCompletedAt = 0;
    this.action = ActionType.CUT;
    this.CCsSelected = ccs.map((cc) => ({
      id: cc.id,
      cannotEdit: cc?.cannotEdit ?? false,
      cannotDelete: cc?.cannotDelete ?? false,
    }));

    // Temporarily allow editing & deleting for the selected CCs so Cut can proceed
    await this.toggleCCStatus(this.CCsSelected.map((cc) => ({ id: cc.id, cannotEdit: false, cannotDelete: false })));

    if (ccs.length > 1) {
      await this.cutMultipleCC();
      store.dispatch(setPanelMessage("CUTTED_SUCCESSFULLY"));
      return true;
    }

    await this.cutSingleCC(ccs[0].id);
    store.dispatch(setPanelMessage("CUTTED_SUCCESSFULLY"));
    return true;
  }

  async copy(): Promise<boolean> {
    const ccs = await this.getCCSelected();
    if (!ccs?.length) return false;

    // A new copy starts a fresh clipboard flow, so allow immediate paste.
    this.lastPasteCompletedAt = 0;
    this.action = ActionType.COPY;
    this.CCsSelected = ccs.map((cc) => ({
      id: cc.id,
      cannotEdit: cc?.cannotEdit ?? false,
      cannotDelete: cc?.cannotDelete ?? false,
    }));

    if (ccs.length > 1) {
      await this.copyMultipleCC();
      store.dispatch(setPanelMessage("COPIED_SUCCESSFULLY"));
      return true;
    }

    await this.copySingleCC(ccs[0].id);
    store.dispatch(setPanelMessage("COPIED_SUCCESSFULLY"));
    return true;
  }

  async paste(): Promise<[boolean, CoObjectItem[] | null]> {
    const now = Date.now();
    const isInCooldown = now - this.lastPasteCompletedAt < CopyCutPaste.PASTE_COOLDOWN_MS;
    if (this.isPasteInProgress || isInCooldown) {
      // Silent no-op: prevent duplicate paste when called twice too quickly.
      return [true, []];
    }

    this.isPasteInProgress = true;
    try {
      const cc = await getParentContentControls();
      if (cc) {
        const coObjectItem = await getCoObjectFromTagOrCustomXmlByContentControlId(cc.id);
        const isFreetext = coObjectItem?.info?.objectType === CoObjectTypeEnum.FREE_TEXT;
        if (!isFreetext) {
          store.dispatch(setPanelMessage("CANNOT_PASTE_INSIDE_CC"));
          console.error("YOU CANNOT PASTE INSIDE A CC");
          return [false, null];
        }
      }

      if (this.action === ActionType.CUT) {
        const pastedCcs = await this.pasteXMLString();
        await this.restoreCCStatus(pastedCcs);
        // Switch to copy after a cut, so subsequent pastes behave as copy
        this.action = ActionType.COPY;
        this.CCsSelected = pastedCcs.map((cc) => ({
          id: cc.id,
          cannotEdit: false,
          cannotDelete: false,
        }));
        const ccs = pastedCcs.map(async (el) => {
          return await this.getCCObj(el.oldId);
        });
        store.dispatch(invalidate());
        return [true, await Promise.all(ccs)];
      }

      if (this.action === ActionType.COPY) {
        const pastedElements = await this.pasteFromCopy();
        store.dispatch(invalidate());
        return [true, pastedElements];
      }

      // No action set -> nothing to paste
      store.dispatch(setPanelMessage("NO_ACTION_TO_PASTE"));
      return [false, null];
    } finally {
      this.isPasteInProgress = false;
      this.lastPasteCompletedAt = Date.now();
    }
  }

  // #region ---- CC UTILITIES ----

  private async getCCSelected(): Promise<ContentControl[] | null> {
    try {
      const selection = await checkIfSelected();
      if (!selection) {
        store.dispatch(setPanelMessage("NO_CC_SELECTED"));
        return null;
      }
      const CCsList = Array.isArray(selection) ? selection : [selection];
      const areDocumentPartSelected = await this.hasDocumentParts(CCsList.map((cc) => cc.id));
      if (areDocumentPartSelected) {
        store.dispatch(setPanelMessage("CANNOT_PERFORM_ACTION_ON_DOCUMENT_PART_INCLUDE"));
        return null;
      }
      return CCsList;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  private async restoreCCStatus(pastedCCs: PastedCCResult[]): Promise<void> {
    const newCCs: DeletableAndEditableCC[] = pastedCCs.reduce<DeletableAndEditableCC[]>((res, pastedCC) => {
      const originalCC = this.CCsSelected.find((o) => pastedCC.oldId === o.id);
      res.push({
        id: pastedCC.id,
        cannotEdit: originalCC?.cannotEdit ?? false,
        cannotDelete: originalCC?.cannotDelete ?? false,
      });
      return res;
    }, []);
    await this.toggleCCStatus(newCCs); // Restore cannotEdit and cannotDelete
  }

  private async toggleCCStatus(ccs: DeletableAndEditableCC[]): Promise<void> {
    const toggleStatusCC = ccs.map(toggleDeleteAndEditCC);
    await Promise.all(toggleStatusCC);
  }

  private async hasDocumentParts(ccIds: number[]): Promise<boolean> {
    const ccList = await getDocumentCoObjects();
    const filteredCCListIds = ccList.reduce<number[]>((acc, cc) => {
      if (cc.info.objectType === "DOCUMENT_PART_INCLUDE") return acc;
      return [...acc, Number(cc.id)];
    }, []);

    for (const id of ccIds) {
      const ccObject = await this.getCCObj(id);
      if (!ccObject) {
        const isIncludedInADocPart = !filteredCCListIds.includes(id);
        if (isIncludedInADocPart) return true;
      }
      const isDocumentPart = ccObject?.info?.objectType === "DOCUMENT_PART_INCLUDE";
      if (isDocumentPart) return true;
    }
    return false;
  }

  private createNewCCObj = (coObject: CoObjectItem): CoObjectItem => {
    const newCoObject = { ...coObject };
    newCoObject.info.objectName += "_Copy";
    delete newCoObject.id;
    return newCoObject;
  };
  //endregion

  // #region ---- GETTING UTILITIES ----
  private async getCCObj(copiedCCId: number): Promise<CoObjectItem | null> {
    return (await getCoObjectFromTagOrCustomXmlByContentControlId(copiedCCId)) ?? null;
  }

  //endregion

  // #region ---- PASTE UTILITIES ----
  private async pasteXMLString(): Promise<PastedCCResult[]> {
    return await Word.run(async (ctx) => {
      try {
        const ccXmlString = updateContentControlAliases(this.ccsXmlString);
        const range = await insertXmlIntoSelection(ctx, ccXmlString);
        range.load(["items"]);
        await ctx.sync();
        const ccInRange = range.getContentControls();
        ccInRange.load(["items", "title", "id", "tag"]);
        await ctx.sync();
        const pastedCCs: PastedCCResult[] = ccInRange.items
          .map((cc) => {
            const [title, oldId] = cc.title.split("_&%");
            cc.title = title;
            if (cc?.tag === null) {
              return null;
            }
            return {
              id: cc.id,
              title,
              oldId: Number(oldId),
            };
          })
          .filter(Boolean);
        await ctx.sync();
        return pastedCCs;
      } catch (err) {
        console.error("ERROR LOOKING FOR CCs AFTER PASTE", err);
        return [];
      }
    });
  }

  private async pasteFromCopy(): Promise<(CoObjectItem | null)[]> {
    const documentId = (await retrieveDocumentProp(DocPropKeys.DocumentId)) ?? EVENT_CHANNEL_DEFAULT_TOPIC;
    const documentPartMetadata = await getDocumentPartMetadataFromCustomXml();
    const username = selectCpmUsername(store.getState());

    const pastedCC = await this.pasteXMLString();

    const insertDBPromises: Promise<CoObjectItem | null>[] = pastedCC.map(async (el) => {
      let coObject = await this.getCCObj(el.oldId);
      coObject = this.createNewCCObj(coObject);

      if (!coObject) {
        await deleteRangeCC(el.id);
        console.error("New CC NOT FOUND IN THE DOCUMENT (index mismatch)");
        return Promise.resolve(null);
      }
      coObject.contentControlId = el.id;
      coObject.documentId = documentId;

      if (coObject.isLocked) {
        await lockObject(coObject, false);
        (coObject as any).isLocked = false;
      }

      const documentPart = await fetchDocumentPartDetails(documentId);
      if (isLegacyComponentInstance(coObject)) {
        coObject = await applyComponentDataToLegacyComponentInstance(coObject, documentPartMetadata);
      } else {
        await applyInheritedInfo(coObject, documentPartMetadata);
      }

      return insertElaboration(
        Number(coObject.contentControlId),
        documentPart,
        coObject,
        documentPartMetadata.language,
        username
      );
    });

    return await Promise.all(insertDBPromises);
  }

  //endregion

  // #region ---- COPY UTILITIES ----
  private async copySingleCC(ccId: number): Promise<void> {
    const xmlString = await getXmlByCCId(ccId);
    this.setccsXmlString(xmlString);
  }

  private async copyMultipleCC(): Promise<void> {
    const xmlString = await getXMLFromSelection();
    this.setccsXmlString(xmlString);
  }

  //endregion

  // #region ---- CUT UTILITIES ----
  private async cutMultipleCC(): Promise<void> {
    await this.copyMultipleCC();
    await deleteRangeSelection();
  }

  private async cutSingleCC(ccId: number): Promise<void> {
    await this.copySingleCC(ccId);
    await deleteRangeCC(ccId);
  }

  //endregion

  // #region ---- CLASS UTILITIES ----
  private setccsXmlString(xmlString: string): void {
    if (!xmlString) {
      this.ccsXmlString = "";
      return;
    }
    this.ccsXmlString = xmlString;
  }
}

export const CopyCutPasteInstance = CopyCutPaste.getInstance();
